/**
 * ProductModal - Premium Fullscreen Image Viewer
 * -----------------------------------------------
 * Features:
 *  - Pinch-to-zoom (image only, browser zoom blocked)
 *  - Pan with boundary constraints
 *  - Swipe-to-navigate gallery (at 1x zoom)
 *  - Double-tap to toggle 1x ↔ 2.5x zoom
 *  - Double-click (desktop) to toggle zoom
 *  - Hardware-accelerated transforms (translate3d + scale)
 *  - Keyboard arrow navigation + ESC close
 *  - No duplicate listeners, no memory leaks
 *  - Compatible: iOS Safari, Android Chrome, Samsung Internet
 */

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {

      // ─────────────────────────────────────────────
      // LIFECYCLE
      // ─────────────────────────────────────────────

      constructor() {
        super();

        // Zoom state
        this._zoom       = 1;
        this._panX       = 0;
        this._panY       = 0;
        this._minZoom    = 1;
        this._maxZoom    = 5;
        this._doubleTapZoom = 2.5;

        // Gesture state
        this._isDragging  = false;
        this._isPinching  = false;
        this._isSwiping   = false;

        // Drag
        this._dragStartX = 0;
        this._dragStartY = 0;

        // Pinch
        this._pinchStartDist  = 0;
        this._pinchStartZoom  = 1;
        this._pinchMidX       = 0;  // midpoint of two fingers
        this._pinchMidY       = 0;
        this._pinchOriginPanX = 0;
        this._pinchOriginPanY = 0;

        // Swipe
        this._swipeStartX    = 0;
        this._swipeStartY    = 0;
        this._swipeStartTime = 0;

        // Double tap
        this._lastTapTime   = 0;
        this._lastTapX      = 0;
        this._lastTapY      = 0;

        // Active media
        this.activeImg = null;

        // Bind so we can remove cleanly
        this._onMouseDown = this._handleMouseDown.bind(this);
        this._onMouseMove = this._handleMouseMove.bind(this);
        this._onMouseUp   = this._handleMouseUp.bind(this);
        this._onKeyDown   = this._handleKeyDown.bind(this);

        // Setup UI controls
        this._setupZoomBar();

        // Setup all touch events on the container (not on img)
        // This fires once at construction; activeImg is set later via setupImagePan()
        this._setupTouchEvents();

        // Keyboard navigation
        document.addEventListener('keydown', this._onKeyDown);
      }

      // ─────────────────────────────────────────────
      // MODAL SHOW / HIDE
      // ─────────────────────────────────────────────

      show(opener) {
        super.show(opener);
        this._showActiveMedia();
      }

      hide() {
        super.hide();
        this._resetState();
      }

      // ─────────────────────────────────────────────
      // MEDIA MANAGEMENT
      // ─────────────────────────────────────────────

      _showActiveMedia() {
        this._resetState();

        const openerMediaId = this.openedBy.getAttribute('data-media-id').split('-').pop();
        const allMedia      = Array.from(this.querySelectorAll('[data-media-id]'));

        allMedia.forEach(el => el.classList.remove('active'));

        // Match by the raw numeric suffix to handle section-prefixed IDs
        const target = allMedia.find(el =>
          el.getAttribute('data-media-id').split('-').pop() === openerMediaId
        );

        if (!target) return;

        this._activateMedia(target);
      }

      switchToMedia(index) {
        const allMedia = Array.from(this.querySelectorAll('[data-media-id]'));
        if (index < 0 || index >= allMedia.length) return;

        const currentActive = allMedia.find(el => el.classList.contains('active'));
        const nextMedia     = allMedia[index];

        if (!nextMedia || currentActive === nextMedia) return;

        this._resetState();

        // Instantly deactivate all, activate next
        allMedia.forEach(el => el.classList.remove('active'));
        this._activateMedia(nextMedia);
        this._updateNavArrows();
      }

      _activateMedia(mediaEl) {
        mediaEl.classList.add('active');

        if (mediaEl.nodeName === 'IMG') {
          this._setupImagePan(mediaEl);
          // Initial transform: reset to identity so zoom starts clean
          mediaEl.style.transition = 'none';
          mediaEl.style.transform  = 'translate3d(0px, 0px, 0) scale(1)';
          mediaEl.style.opacity    = '1';
        } else {
          this.activeImg = null;
        }

        // Autoload YouTube deferred
        const tmpl = mediaEl.querySelector('template');
        if (
          mediaEl.nodeName === 'DEFERRED-MEDIA' &&
          tmpl &&
          tmpl.content.querySelector('.js-youtube')
        ) {
          mediaEl.loadContent();
        }
      }

      // ─────────────────────────────────────────────
      // IMAGE PAN SETUP  (mouse drag on desktop)
      // ─────────────────────────────────────────────

      _setupImagePan(img) {
        // Remove old mouse listeners from previous image
        if (this.activeImg) {
          this.activeImg.removeEventListener('mousedown',  this._onMouseDown);
          this.activeImg.removeEventListener('dblclick',   this._onDblClick);
        }

        this.activeImg = img;

        // Ensure GPU compositing layer & touch-action block
        img.style.willChange        = 'transform';
        img.style.touchAction       = 'none';
        img.style.userSelect        = 'none';
        img.style.webkitUserDrag    = 'none';
        img.style.cursor            = 'default';
        img.draggable               = false;

        img.addEventListener('mousedown', this._onMouseDown);

        // Double-click zoom toggle (desktop)
        this._onDblClick = (e) => {
          e.stopPropagation();
          this._toggleDoubleTapZoom(e.clientX, e.clientY);
        };
        img.addEventListener('dblclick', this._onDblClick);

        // Apply initial transform
        this._applyTransform(false);
      }

      // ─────────────────────────────────────────────
      // KEYBOARD NAVIGATION
      // ─────────────────────────────────────────────

      _handleKeyDown(e) {
        if (!this.hasAttribute('open')) return;
        const allMedia = Array.from(this.querySelectorAll('[data-media-id]'));
        const idx      = allMedia.findIndex(el => el.classList.contains('active'));

        if (e.key === 'ArrowRight' && idx < allMedia.length - 1) {
          this.switchToMedia(idx + 1);
        } else if (e.key === 'ArrowLeft' && idx > 0) {
          this.switchToMedia(idx - 1);
        }
        // ESC is already handled by parent ModalDialog
      }

      // ─────────────────────────────────────────────
      // MOUSE HANDLERS  (desktop pan)
      // ─────────────────────────────────────────────

      _handleMouseDown(e) {
        if (this._zoom <= 1) return;
        e.preventDefault();
        this._isDragging = true;
        this.activeImg.style.cursor    = 'grabbing';
        this.activeImg.style.transition = 'none';
        this._dragStartX = e.clientX - this._panX;
        this._dragStartY = e.clientY - this._panY;

        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup',   this._onMouseUp);
      }

      _handleMouseMove(e) {
        if (!this._isDragging) return;
        e.preventDefault();
        this._panX = e.clientX - this._dragStartX;
        this._panY = e.clientY - this._dragStartY;
        this._applyTransform(false);
      }

      _handleMouseUp() {
        if (!this._isDragging) return;
        this._isDragging = false;
        if (this.activeImg) {
          this.activeImg.style.cursor     = this._zoom > 1 ? 'grab' : 'default';
          this.activeImg.style.transition = 'transform 0.15s ease-out';
          this._constrainPan();
        }
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup',   this._onMouseUp);
      }

      // ─────────────────────────────────────────────
      // TOUCH HANDLERS  (mobile)
      // Note: registered on the modal CONTAINER so fingers
      // anywhere on screen are captured — not just over the image.
      // { passive: false } allows e.preventDefault() on iOS Safari.
      // ─────────────────────────────────────────────

      _setupTouchEvents() {
        const opts = { passive: false };

        this.addEventListener('touchstart', (e) => {
          if (!this.activeImg) return;

          if (e.touches.length === 1) {
            this._handleTouchStart1(e);
          } else if (e.touches.length === 2) {
            this._handleTouchStart2(e);
          }
        }, opts);

        this.addEventListener('touchmove', (e) => {
          if (!this.activeImg) return;

          if (this._isPinching && e.touches.length === 2) {
            e.preventDefault();
            this._handlePinchMove(e);
          } else if (this._isDragging && e.touches.length === 1) {
            e.preventDefault();
            this._handleDragMove(e);
          } else if (this._isSwiping && e.touches.length === 1) {
            const dX = e.touches[0].clientX - this._swipeStartX;
            const dY = e.touches[0].clientY - this._swipeStartY;
            // Block horizontal scrolling while swiping images
            if (Math.abs(dX) > Math.abs(dY)) e.preventDefault();
          }
        }, opts);

        this.addEventListener('touchend', (e) => {
          if (!this.activeImg) return;
          this._handleTouchEnd(e);
        }, opts);

        // Prevent iOS two-finger page zoom via CSS as well
        this.style.touchAction = 'none';
      }

      // Single touch start — could be pan, swipe, or double-tap
      _handleTouchStart1(e) {
        const t   = e.touches[0];
        const now = Date.now();

        // ── Double-tap detection ──
        const dT = now - this._lastTapTime;
        const dX = Math.abs(t.clientX - this._lastTapX);
        const dY = Math.abs(t.clientY - this._lastTapY);

        if (dT < 300 && dX < 30 && dY < 30) {
          // It's a double-tap!
          e.preventDefault();
          this._toggleDoubleTapZoom(t.clientX, t.clientY);
          this._lastTapTime = 0; // reset so triple-tap doesn't re-trigger
          return;
        }

        this._lastTapTime = now;
        this._lastTapX    = t.clientX;
        this._lastTapY    = t.clientY;

        this._isPinching = false;

        if (this._zoom > 1) {
          // Pan mode — drag the zoomed image
          this._isDragging = true;
          this._isSwiping  = false;
          if (this.activeImg) this.activeImg.style.transition = 'none';
          this._dragStartX = t.clientX - this._panX;
          this._dragStartY = t.clientY - this._panY;
        } else {
          // Swipe mode — navigate between images
          this._isSwiping      = true;
          this._isDragging     = false;
          this._swipeStartX    = t.clientX;
          this._swipeStartY    = t.clientY;
          this._swipeStartTime = now;
        }
      }

      // Two-finger touch start — pinch begin
      _handleTouchStart2(e) {
        e.preventDefault();  // Critical: block iOS page zoom from the very first event

        this._isDragging = false;
        this._isSwiping  = false;
        this._isPinching = true;

        const t0 = e.touches[0];
        const t1 = e.touches[1];

        // Initial pinch distance
        const dx             = t0.clientX - t1.clientX;
        const dy             = t0.clientY - t1.clientY;
        this._pinchStartDist = Math.hypot(dx, dy);
        this._pinchStartZoom = this._zoom;

        // Midpoint of two fingers in screen coords
        this._pinchMidX = (t0.clientX + t1.clientX) / 2;
        this._pinchMidY = (t0.clientY + t1.clientY) / 2;

        // Remember pan at pinch start (we'll adjust relative to finger midpoint)
        this._pinchOriginPanX = this._panX;
        this._pinchOriginPanY = this._panY;

        if (this.activeImg) this.activeImg.style.transition = 'none';
      }

      // Two-finger pinch move — update zoom
      _handlePinchMove(e) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];

        const dx   = t0.clientX - t1.clientX;
        const dy   = t0.clientY - t1.clientY;
        const dist = Math.hypot(dx, dy);

        const scale   = dist / this._pinchStartDist;
        const newZoom = Math.max(
          this._minZoom,
          Math.min(this._maxZoom, this._pinchStartZoom * scale)
        );

        // Zoom origin: midpoint of two fingers relative to image centre
        if (this.activeImg) {
          const rect      = this.activeImg.getBoundingClientRect();
          const imgCentreX = rect.left + rect.width  / 2;
          const imgCentreY = rect.top  + rect.height / 2;

          const originX  = this._pinchMidX - imgCentreX;
          const originY  = this._pinchMidY - imgCentreY;

          // Shift pan so the point under the fingers stays fixed
          const zoomRatio = newZoom / this._zoom;
          this._panX      = originX - zoomRatio * (originX - this._panX);
          this._panY      = originY - zoomRatio * (originY - this._panY);
        }

        this._zoom = newZoom;
        this._updateZoomIndicator();
        this._applyTransform(false);
      }

      // Single-touch drag move — pan the zoomed image
      _handleDragMove(e) {
        const t  = e.touches[0];
        this._panX = t.clientX - this._dragStartX;
        this._panY = t.clientY - this._dragStartY;
        this._applyTransform(false);
      }

      // Touch end — finalise gesture
      _handleTouchEnd(e) {
        // Evaluate swipe
        if (this._isSwiping && e.changedTouches.length > 0) {
          const t        = e.changedTouches[0];
          const dX       = t.clientX - this._swipeStartX;
          const dY       = t.clientY - this._swipeStartY;
          const duration = Date.now() - this._swipeStartTime;

          const isHorizontalSwipe =
            duration < 350 &&
            Math.abs(dX) > 45 &&
            Math.abs(dY) < Math.abs(dX) * 0.6;

          if (isHorizontalSwipe) {
            const allMedia = Array.from(this.querySelectorAll('[data-media-id]'));
            const idx      = allMedia.findIndex(el => el.classList.contains('active'));

            if (dX < 0 && idx < allMedia.length - 1) {
              this.switchToMedia(idx + 1, -1);   // swipe left → next
              return;
            } else if (dX > 0 && idx > 0) {
              this.switchToMedia(idx - 1, +1);   // swipe right → prev
              return;
            }
          }
        }

        // Reset gesture flags and snap pan
        this._isDragging = false;
        this._isPinching = false;
        this._isSwiping  = false;

        if (this.activeImg) {
          this.activeImg.style.transition = 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          this._constrainPan();
        }

        this._updateCursor();
      }

      // ─────────────────────────────────────────────
      // DOUBLE-TAP / DOUBLE-CLICK ZOOM TOGGLE
      // ─────────────────────────────────────────────

      _toggleDoubleTapZoom(clientX, clientY) {
        if (!this.activeImg) return;

        const targetZoom = this._zoom > 1 ? 1 : this._doubleTapZoom;

        if (targetZoom === 1) {
          // Zoom out: just reset
          this._zoom = 1;
          this._panX = 0;
          this._panY = 0;
        } else {
          // Zoom in centred on the tap point
          const rect      = this.activeImg.getBoundingClientRect();
          const originX   = clientX - (rect.left + rect.width  / 2);
          const originY   = clientY - (rect.top  + rect.height / 2);
          const ratio     = targetZoom / this._zoom;
          this._panX      = originX - ratio * (originX - this._panX);
          this._panY      = originY - ratio * (originY - this._panY);
          this._zoom      = targetZoom;
        }

        this.activeImg.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        this._constrainPan();  // also calls _applyTransform
        this._updateZoomIndicator();
        this._updateCursor();
      }

      // ─────────────────────────────────────────────
      // ZOOM BAR (button controls)
      // ─────────────────────────────────────────────

      _setupZoomBar() {
        const dialog = this.querySelector('[role="dialog"]');
        if (!dialog || this.querySelector('.product-modal__zoom-controls')) return;

        const bar = document.createElement('div');
        bar.className = 'product-modal__zoom-controls';
        bar.setAttribute('aria-label', 'Image zoom controls');
        bar.innerHTML = `
          <button type="button" class="zoom-btn zoom-out" aria-label="Zoom out">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19.5 12h-15"/>
            </svg>
          </button>
          <span class="zoom-level" aria-live="polite" aria-atomic="true">100%</span>
          <button type="button" class="zoom-btn zoom-in" aria-label="Zoom in">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
          </button>
          <button type="button" class="zoom-btn zoom-reset" aria-label="Reset zoom">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/>
            </svg>
          </button>
          <span class="zoom-divider" aria-hidden="true"></span>
          <button type="button" class="zoom-btn zoom-close" aria-label="Close fullscreen viewer">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        `;

        dialog.appendChild(bar);

        bar.querySelector('.zoom-in').addEventListener('click',    (e) => { e.stopPropagation(); this._stepZoom(+0.5); });
        bar.querySelector('.zoom-out').addEventListener('click',   (e) => { e.stopPropagation(); this._stepZoom(-0.5); });
        bar.querySelector('.zoom-reset').addEventListener('click', (e) => { e.stopPropagation(); this._resetZoom(); });
        bar.querySelector('.zoom-close').addEventListener('click', (e) => { e.stopPropagation(); this.hide(); });

        // ── Desktop prev/next arrow buttons ──
        const prevBtn = document.createElement('button');
        prevBtn.type = 'button';
        prevBtn.className = 'product-modal__nav-btn product-modal__nav-prev';
        prevBtn.setAttribute('aria-label', 'Previous image');
        prevBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';

        const nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'product-modal__nav-btn product-modal__nav-next';
        nextBtn.setAttribute('aria-label', 'Next image');
        nextBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';

        dialog.appendChild(prevBtn);
        dialog.appendChild(nextBtn);

        prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const all = Array.from(this.querySelectorAll('[data-media-id]'));
          const idx = all.findIndex(el => el.classList.contains('active'));
          if (idx > 0) this.switchToMedia(idx - 1);
        });

        nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const all = Array.from(this.querySelectorAll('[data-media-id]'));
          const idx = all.findIndex(el => el.classList.contains('active'));
          if (idx < all.length - 1) this.switchToMedia(idx + 1);
        });

        this._navPrev = prevBtn;
        this._navNext = nextBtn;
        this._updateNavArrows();
      }

      _updateNavArrows() {
        if (!this._navPrev || !this._navNext) return;
        const all = Array.from(this.querySelectorAll('[data-media-id]'));
        const idx = all.findIndex(el => el.classList.contains('active'));
        this._navPrev.disabled = idx <= 0;
        this._navNext.disabled = idx >= all.length - 1;
        this._navPrev.style.opacity = idx <= 0 ? '0.35' : '1';
        this._navNext.style.opacity = idx >= all.length - 1 ? '0.35' : '1';
      }


      // ─────────────────────────────────────────────
      // TRANSFORM HELPERS
      // ─────────────────────────────────────────────

      /**
       * Apply hardware-accelerated transform using translate3d + scale.
       * This puts the element on its own GPU compositing layer.
       * @param {boolean} animate — whether to use a CSS transition
       */
      _applyTransform(animate = false) {
        if (!this.activeImg) return;

        this.activeImg.style.transition = animate
          ? 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          : 'none';

        // translate3d triggers GPU compositing; scale is then applied on top
        this.activeImg.style.transform =
          `translate3d(${this._panX}px, ${this._panY}px, 0) scale(${this._zoom})`;
      }

      _constrainPan() {
        if (!this.activeImg) return;

        if (this._zoom <= 1) {
          this._panX = 0;
          this._panY = 0;
        } else {
          // Maximum pan = half the extra size added by zoom
          const maxX = (this.activeImg.clientWidth  * (this._zoom - 1)) / 2;
          const maxY = (this.activeImg.clientHeight * (this._zoom - 1)) / 2;
          this._panX = Math.max(-maxX, Math.min(maxX, this._panX));
          this._panY = Math.max(-maxY, Math.min(maxY, this._panY));
        }

        this._applyTransform(true);
      }

      _stepZoom(delta) {
        if (!this.activeImg) return;
        this._zoom = Math.max(this._minZoom, Math.min(this._maxZoom, this._zoom + delta));

        if (this._zoom === 1) { this._panX = 0; this._panY = 0; }

        this._constrainPan();
        this._updateZoomIndicator();
        this._updateCursor();
      }

      _resetZoom() {
        this._zoom = 1;
        this._panX = 0;
        this._panY = 0;

        if (this.activeImg) {
          this.activeImg.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          this.activeImg.style.transform  = 'translate3d(0px, 0px, 0) scale(1)';
          this.activeImg.style.cursor     = 'default';
        }

        this._updateZoomIndicator();
      }

      _resetState() {
        this._zoom       = 1;
        this._panX       = 0;
        this._panY       = 0;
        this._isDragging = false;
        this._isPinching = false;
        this._isSwiping  = false;
        this._lastTapTime = 0;

        if (this.activeImg) {
          this.activeImg.style.transform  = 'translate3d(0px, 0px, 0) scale(1)';
          this.activeImg.style.transition = '';
          this.activeImg.style.cursor     = 'default';
        }
        this.activeImg = null;
        this._updateZoomIndicator();
      }

      _updateZoomIndicator() {
        const el = this.querySelector('.zoom-level');
        if (el) el.textContent = `${Math.round(this._zoom * 100)}%`;
      }

      _updateCursor() {
        if (!this.activeImg) return;
        this.activeImg.style.cursor = this._zoom > 1 ? 'grab' : 'default';
      }

      // Legacy alias (kept so any external callers still work)
      resetZoom() { this._resetZoom(); }
      zoom(delta) { this._stepZoom(delta); }
    }
  );
}
